import React from "react";
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface ApartmentMetricsData {
    date: string;
    [key: string]: string | number;
}

interface ApartmentMetricsChartProps {
    data: ApartmentMetricsData[];
    metric: string;
    formatter: (value: number) => string;
}

const ApartmentMetricChart: React.FC<ApartmentMetricsChartProps> = ({ data, metric, formatter }) => {
    const transformedData = data.map((item) => ({
        name: item.date,
        ...Object.fromEntries(
            Object.entries(item)
                .filter(([key]) => key !== "date")
                .map(([key, value]) => [key, typeof value === "string" ? parseFloat(value) : value]),
        ),
    }));

    const apartmentNames = Object.keys(transformedData[0] || {}).filter((key) => key !== "name");

    function getApartmentColor(apartment: string) {
        const colorList = [
            "#1e40af", // dark blue (blue-800)
            "#f4a959", // volt/orange (orange-500)
            "#1e293b", // slate-800
            "#64748b", // slate-500
            "#cbd5e1", // slate-300
            "#55075e", // orange-500
            "#e5e7eb", // slate-300
            "#2c533a", // green-500
            "#ef4444", // red-500
            "#0f212c", // dark blue (blue-800) for trend line
        ];
        return colorList[apartmentNames.indexOf(apartment) % colorList.length];
    }

    return (
        <>
            <h3 className={"font-semibold mb-8"}>{metric} by Apartment</h3>
            <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={transformedData}>
                        <XAxis dataKey="name" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} tickFormatter={formatter} />
                        <Tooltip
                            formatter={(value: number | undefined, name: string | undefined) => [value !== undefined ? formatter(value) : "", name ?? ""]}
                            contentStyle={{
                                backgroundColor: "white",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                padding: "8px",
                            }}
                            cursor={{ fill: "#f8fafc" }} // Light hover effect (slate-50)
                        />
                        <Legend />
                        {apartmentNames.map((apartment) => (
                            <Bar key={apartment} dataKey={apartment} fill={getApartmentColor(apartment)} radius={[4, 4, 0, 0]} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </>
    );
};

export default ApartmentMetricChart;
